import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mic, Send, Sparkles, TrendingUp, TrendingDown, Plus, Check, ArrowRight, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { parsePriceDictation, applyPriceUpdates, formatMXN, normalizeTTS } from "@/lib/api";

const EXAMPLES = [
  "Kilo de mango 30 pesos, kilo de naranja 25 pesos, kilo de guayaba 12 pesos",
  "El jitomate subió a 35 pesos el kilo, la cebolla a 18 pesos y el limón a 40 el kilo",
  "Bolillo 4 pesos la pieza, kilo de fresa 60 pesos y refresco 22 pesos cada uno",
];

export default function PriceDictationModal({ open, onClose, onApplied }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [recording, setRecording] = useState(false);
  const [micSupported, setMicSupported] = useState(true);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (open) {
      setText(""); setPreview(null);
      setTimeout(() => inputRef.current?.focus(), 250);
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setMicSupported(!!SR);
    return () => {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [open]);

  const submit = async (override) => {
    const value = (override ?? text).trim();
    if (!value) return toast.error("Dicta o escribe los precios");
    setLoading(true); setPreview(null);
    try {
      const r = await parsePriceDictation(value);
      if (r.detected_count === 0) {
        toast.error("No detecté precios. Prueba otra vez.");
      } else {
        setPreview(r);
      }
    } catch (e) {
      toast.error("No pude procesar el dictado");
    } finally { setLoading(false); }
  };

  const applyAll = async () => {
    if (!preview?.preview?.length) return;
    setApplying(true);
    try {
      const payload = preview.preview.map((it) => ({
        action: it.action,
        product_id: it.product_id,
        name: it.name,
        new_price: it.new_price,
        category: it.category,
        unit: it.unit,
      }));
      const r = await applyPriceUpdates(payload);
      speak(r.tts_message);
      toast.success(`${r.updated_count} actualizados · ${r.created_count} nuevos`);
      onApplied?.();
      // Reset for another round without closing so the tendero can hear the reply
      setPreview(null);
      setText("");
    } catch (e) {
      toast.error("No pude aplicar los cambios");
    } finally { setApplying(false); }
  };

  const speak = async (msg) => {
    try {
      if (!msg || !window.speechSynthesis) return;
      const spoken = await normalizeTTS(msg);
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(spoken);
      u.lang = "es-MX"; u.rate = 1.02;
      window.speechSynthesis.speak(u);
    } catch { /* noop */ }
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Micrófono no soportado — escribe el dictado"); return; }
    if (recording) {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      setRecording(false);
      return;
    }
    try {
      const rec = new SR();
      rec.lang = "es-MX"; rec.interimResults = true; rec.continuous = true; rec.maxAlternatives = 1;
      let finalText = text;
      rec.onstart = () => setRecording(true);
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) finalText += t; else interim += t;
        }
        setText(finalText + interim);
      };
      rec.onerror = (e) => { setRecording(false); if (e.error === "not-allowed") toast.error("Permite el micrófono"); };
      rec.onend = () => setRecording(false);
      recognitionRef.current = rec;
      rec.start();
    } catch { setRecording(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="pdictation-modal"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 md:p-8 max-h-[90vh] overflow-auto"
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white p-2" data-testid="pdictation-close">
              <X size={18} />
            </button>

            <div className="chip mb-3"><Sparkles size={12} className="text-[#ccff00]" /> Precios del día</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter leading-none">
              Dicta los <span className="text-[#ccff00]">precios de hoy</span>.
              <br className="hidden md:block" /> CHALANN los aplica en un tap.
            </h2>
            <p className="mt-3 text-white/60 text-sm max-w-2xl">
              Habla como en la central de abastos. Puedes decir varios productos en una sola frase.
            </p>

            {/* Input */}
            <div className="mt-6 flex items-start gap-3">
              <button
                type="button"
                onClick={toggleMic}
                className={`flex-shrink-0 w-14 h-14 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${
                  recording ? "border-[#ccff00] bg-[#ccff00]/15" : "border-[#ff5a00] bg-[#ff5a00]/10 hover:bg-[#ff5a00]/20"
                }`}
                data-testid="pdictation-mic"
                title={micSupported ? "Tocar para dictar" : "Micrófono no soportado — escribe"}
              >
                <Mic size={22} className={recording ? "text-[#ccff00]" : "text-[#ff5a00]"} />
                {recording && (
                  <motion.span
                    className="absolute w-14 h-14 rounded-full border-2 border-[#ccff00]"
                    animate={{ scale: [1, 1.35], opacity: [0.8, 0] }}
                    transition={{ duration: 1.4, repeat: Infinity }}
                  />
                )}
              </button>
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={'Ej. "Kilo de mango 30 pesos, kilo de naranja 25 pesos y kilo de guayaba 12 pesos"'}
                  rows={3}
                  className="w-full bg-black/50 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 text-sm md:text-base focus:outline-none focus:border-[#ccff00] resize-none font-body"
                  data-testid="pdictation-input"
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {EXAMPLES.map((ex, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { setText(ex); submit(ex); }}
                      className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 text-white/60 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                      data-testid={`pdictation-example-${i}`}
                    >
                      Ej {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => submit()}
                    disabled={loading}
                    className="ml-auto btn-primary text-sm disabled:opacity-60"
                    data-testid="pdictation-submit"
                  >
                    {loading ? "Procesando…" : (<><Send size={14} /> Procesar dictado</>)}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview */}
            <AnimatePresence>
              {preview && preview.preview.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="mt-8"
                  data-testid="pdictation-preview"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="chip !bg-[#ccff00] !text-black !border-[#ccff00]"><Sparkles size={11} /> Precios del Día Actualizados</span>
                    <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase">
                      {preview.detected_count} detectados
                    </span>
                  </div>
                  <div className="space-y-2">
                    {preview.preview.map((it, i) => (
                      <PreviewRow key={i} row={it} index={i} />
                    ))}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <button
                      onClick={applyAll}
                      disabled={applying}
                      className="flex-1 rounded-2xl bg-[#7dff5a] hover:bg-[#9dff85] text-black py-4 font-display text-lg tracking-tighter flex items-center justify-center gap-2 disabled:opacity-60 transition-colors duration-200 shadow-[0_15px_40px_-15px_rgba(125,255,90,0.55)]"
                      data-testid="pdictation-apply"
                    >
                      <Check size={18} strokeWidth={3} />
                      {applying ? "Aplicando…" : `Confirmar y guardar todos (${preview.preview.length})`}
                      <ArrowRight size={14} />
                    </button>
                    <button
                      onClick={() => setPreview(null)}
                      className="btn-ghost"
                      data-testid="pdictation-redo"
                    >
                      Volver a dictar
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PreviewRow({ row, index }) {
  const isCreate = row.action === "create";
  const delta = row.old_price != null ? row.new_price - row.old_price : null;
  const arrow = isCreate ? <Plus size={14} className="text-[#ccff00]" /> :
    (delta > 0 ? <TrendingUp size={14} className="text-[#ff5a00]" /> :
     delta < 0 ? <TrendingDown size={14} className="text-[#ccff00]" /> :
     <ArrowRight size={14} className="text-white/50" />);
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-3 md:p-4 flex items-center gap-3" data-testid={`pdictation-row-${index}`}>
      <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
        {arrow}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-sm truncate">{row.name}</p>
          {isCreate ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ccff00]/20 border border-[#ccff00]/40 text-[9px] font-mono uppercase tracking-widest text-[#ccff00] font-bold">
              Nuevo
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-[9px] font-mono uppercase tracking-widest text-white/60">
              Actualiza · {Math.round((row.match_confidence || 0) * 100)}%
            </span>
          )}
        </div>
        <p className="font-mono text-[10px] text-white/40 mt-0.5">
          {row.category} · dictado: “{row.raw}”
        </p>
      </div>
      <div className="text-right flex items-baseline gap-2">
        {isCreate ? (
          <>
            <span className="font-mono text-xs text-white/40">—</span>
            <span className="text-white/40">➔</span>
            <span className="font-display text-lg tracking-tighter text-[#ccff00]">
              {formatMXN(row.new_price)}
              <span className="text-white/40 text-xs font-mono ml-1">/{row.unit || "kg"}</span>
            </span>
          </>
        ) : (
          <>
            <span className="font-mono text-xs text-white/50 line-through">{formatMXN(row.old_price)}</span>
            <span className="text-white/40">➔</span>
            <span className={`font-display text-lg tracking-tighter ${delta > 0 ? "text-[#ff5a00]" : delta < 0 ? "text-[#ccff00]" : "text-white"}`}>
              {formatMXN(row.new_price)}
              <span className="text-white/40 text-xs font-mono ml-1">/{row.unit || "kg"}</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export { Volume2 };
